import { RoundTagGroup } from '@/components/words/round-tag-group';
import type { NoyauPonderation, ReasoningSteps as ReasoningStepsTuple } from '@/schemas/word';
import { useState } from 'react';

interface ReasoningStepsProps {
  steps: ReasoningStepsTuple;
  readOnly: boolean;
  onChange: (next: ReasoningStepsTuple) => void;
}

/**
 * "Étapes de raisonnement" (Point 4): 6 fixed steps, each just a noyau
 * ponderation, rendered under the body and above "Voir aussi". Collapsible,
 * collapsed by default. Each step's {@link RoundTagGroup} is fully independent
 * — editing one never touches another step or the word's own header
 * ponderation, since each gets its own slice of `steps` and its own
 * `onChange` callback that only ever replaces that one index.
 *
 * Read-only mirrors the page's read-only mode (no −/+ controls); the section
 * itself still renders (collapsed) so a reader can expand it to see the steps.
 */
export function ReasoningSteps({ steps, readOnly, onChange }: ReasoningStepsProps) {
  const [expanded, setExpanded] = useState(false);

  function handleStepChange(index: number, next: NoyauPonderation) {
    const copy = [...steps] as ReasoningStepsTuple;
    copy[index] = next;
    onChange(copy);
  }

  return (
    <section className="words-reasoning">
      <button
        type="button"
        className="words-reasoning__toggle"
        aria-expanded={expanded}
        aria-controls="words-reasoning-steps"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="material-symbols-rounded words-reasoning__chevron">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
        Étapes de raisonnement
      </button>

      {expanded && (
        <div id="words-reasoning-steps" className="words-reasoning__steps">
          {steps.map((step, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the 6 steps are fixed-position slots, not a reorderable/keyed list.
            <div key={i} className="words-reasoning__step">
              <span className="words-reasoning__step-label">Étape {i + 1}</span>
              <RoundTagGroup
                noyauPonderation={step}
                editable={!readOnly}
                onChange={(next) => handleStepChange(i, next)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
